import React from "react";
import { Route, Navigate, Routes, useLocation } from "react-router-dom";
import { AuthLayout } from "src/components/AuthLayout";
import { ContentLayout } from "src/components/ContentLayout";
import { LogoutComponent } from "src/components/LogoutComponent/Logout";
import {
  PATH_PUBLIC_LOGIN,
  PATH_ROOT,
  FIRST_CONNECTION_PAGE,
  RESET_PASSWORD_PAGE,
  VERIFY_CREDENTIALS_PAGE,
  VERIFY_PASSWORD_UPDATE_PAGE,
  VERIFY_BANKING_INFORMATION_UPDATE_PAGE,
  LOGIN_PAGE,
  TEMPORARY_LOGIN_PAGE,
  LOGIN_ASSOCIATION_PAGE,
  PATH_FIRST_CONNECTION,
  PATH_LOGIN_ASSOCIATION,
  PATH_RESET_PASSWORD,
  PATH_TEMPORARY_LOGIN,
  PATH_VERIFY_CREDENTIALS,
  VERIFY_BANKING_INFORMATION_UPDATE_PATH,
  VERIFY_PASSWORD_UPDATE_PATH,
  PATH_ACCESSIBILITY,
  PATH_MULTI_ANNUAL_PLAN,
} from "src/constants";
import Accessibility from "src/pages/Accessibility";
import MultiAnnualPlan from "src/pages/MultiAnnualPlan";
import { useAuthStore } from "src/stores/auth-store";

import { FeatureFlagsWrapper } from "../FeatureFlagsWrapper";
import { GetMeWrapper } from "../GetMeWrapper";
import lazyWithRetry from "../LazyWithRetry";
import { AuthenticatedPages } from "../Router";

const FirstConnection = lazyWithRetry(
  () => import("src/pages/FirstConnection"),
);
const Login = lazyWithRetry(() => import("src/pages/Login"));
const ResetPassword = lazyWithRetry(() => import("src/pages/ResetPassword"));
const Root = lazyWithRetry(() => import("src/pages/Root"));
const TemporaryLogin = lazyWithRetry(() => import("src/pages/TemporaryLogin"));
const VerifyAssociation = lazyWithRetry(
  () => import("src/pages/VerifyAssociation"),
);
const VerifyBankingInformationUpdate = lazyWithRetry(
  () => import("src/pages/VerifyBankingInformationUpdate"),
);
const VerifyCredentials = lazyWithRetry(
  () => import("src/pages/VerifyCredentials"),
);
const VerifyPasswordUpdate = lazyWithRetry(
  () => import("src/pages/VerifyPasswordUpdate"),
);

const LoginCallback = lazyWithRetry(() => import("src/pages/LoginCallback"));

const AppRoutes = () => {
  const { pathname } = useLocation();
  const { token, ssoToken } = useAuthStore();
  const isTemporaryLoginPath = pathname === PATH_TEMPORARY_LOGIN;
  const hasAuth = isTemporaryLoginPath ? Boolean(ssoToken) : Boolean(token);

  const hasPrivateLoginPathname = [
    PATH_FIRST_CONNECTION,
    PATH_TEMPORARY_LOGIN,
    PATH_VERIFY_CREDENTIALS,
    PATH_RESET_PASSWORD,
    VERIFY_BANKING_INFORMATION_UPDATE_PATH,
    VERIFY_PASSWORD_UPDATE_PATH,
  ].some((path) => pathname.includes(path));

  const isContentLayoutPath = [PATH_ACCESSIBILITY, PATH_MULTI_ANNUAL_PLAN].some(
    (path) => pathname.includes(path),
  );

  if (isContentLayoutPath) {
    return (
      <ContentLayout>
        <Routes>
          <Route path={PATH_ACCESSIBILITY} element={<Accessibility />} />
          <Route path={PATH_MULTI_ANNUAL_PLAN} element={<MultiAnnualPlan />} />
        </Routes>
      </ContentLayout>
    );
  }

  if (pathname === PATH_LOGIN_ASSOCIATION) {
    return (
      <AuthLayout>
        <Routes>
          <Route
            path={LOGIN_ASSOCIATION_PAGE}
            element={<VerifyAssociation />}
          />
        </Routes>
      </AuthLayout>
    );
  }

  return (
    <>
      {!hasAuth && (
        <AuthLayout>
          <Routes>
            <Route
              path="*"
              element={<Navigate replace to={PATH_PUBLIC_LOGIN} />}
            />
            <Route path="/login/callback" element={<LoginCallback />} />
            <Route element={<Navigate replace to={PATH_PUBLIC_LOGIN} />} />
            <Route path={PATH_ROOT} element={<Root />} />
            <Route path={FIRST_CONNECTION_PAGE} element={<FirstConnection />} />
            <Route path={RESET_PASSWORD_PAGE} element={<ResetPassword />} />
            <Route
              path={VERIFY_CREDENTIALS_PAGE}
              element={<VerifyCredentials />}
            />
            <Route
              path={VERIFY_PASSWORD_UPDATE_PAGE}
              element={<VerifyPasswordUpdate />}
            />
            <Route
              path={VERIFY_BANKING_INFORMATION_UPDATE_PAGE}
              element={<VerifyBankingInformationUpdate />}
            />
            <Route
              path="*"
              element={<Navigate replace to={PATH_PUBLIC_LOGIN} />}
            />
            <Route path={LOGIN_PAGE} element={<Login />} />
          </Routes>
        </AuthLayout>
      )}
      {hasAuth && (
        <FeatureFlagsWrapper>
          <GetMeWrapper>
            {hasPrivateLoginPathname ? (
              <AuthLayout>
                <Routes>
                  <Route
                    path={FIRST_CONNECTION_PAGE}
                    element={<LogoutComponent />}
                  />
                  <Route
                    path={TEMPORARY_LOGIN_PAGE}
                    element={<TemporaryLogin />}
                  />
                  <Route
                    path={VERIFY_CREDENTIALS_PAGE}
                    element={<LogoutComponent />}
                  />
                  <Route
                    path={RESET_PASSWORD_PAGE}
                    element={<LogoutComponent />}
                  />
                  <Route
                    path={VERIFY_PASSWORD_UPDATE_PAGE}
                    element={<VerifyPasswordUpdate />}
                  />
                  <Route
                    path={VERIFY_BANKING_INFORMATION_UPDATE_PAGE}
                    element={<VerifyBankingInformationUpdate />}
                  />
                </Routes>
              </AuthLayout>
            ) : (
              <AuthenticatedPages />
            )}
          </GetMeWrapper>
        </FeatureFlagsWrapper>
      )}
    </>
  );
};

export default AppRoutes;
